import React from 'react'
import {
  CaretDown,
  Download,
  ListBullets,
  MagnifyingGlass,
  SortAscending,
} from '@phosphor-icons/react'
import { Dropdown } from 'antd'
import './styles/header.scss'
import { GetEventAttendeesParams } from 'types/event/eventAttendance'
import { useParams } from 'react-router-dom'
import {
  useGetEventAttendeesExport,
  useGetExportEventTickets,
} from 'api/Event/Attendance'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { eventAttendanceStore } from 'state/event/eventAttendance'
import ExportAttendeesModal from './Modals/ExportAttendeesModal'
import ExportTicketReferenceModal from './Modals/ExportTicketReferenceModal'
import ImportRegistrationsModal from './Modals/ImportRegistrationsModal'
import CheckInOutModal from './Modals/CheckInOut/CheckInOutModal'
import CheckInModal from './Modals/DetailedCheckin/CheckInModal'
import CheckOutModal from './Modals/DetailedCheckout/CheckOutModal'
import DropInModal from './Modals/DropIn/DropInModal'
import AddDropIn from './Modals/AddDropInAttendee/AddDropIn'
import DropOutModal from './Modals/Dropout/DropOutModal'
import FamilyNotFound from './Modals/Dropout/FamilyNotFound'
import FamilyFound from './Modals/Dropout/FamilyFound'
import DetailedDropout from './Modals/Dropout/DetailedDropout'
import DropInDetailModal from './Modals/DropIn/DropInDetailModal'
import ApproveCheckInModal from './Modals/ApproveCheckIn/ApproveCheckInModal'
import RollCallModal from './Modals/RollCall/RollCallModal'
import AddMember from './Modals/Dropout/AddMember'
import { adultStore } from 'state/family/adult'
import { kidStore } from 'state/family/kid'
import KidCreation from 'pages/Family/Profiles/FamilyProfile/FamilyMembers/Modals/Kid/KidCreation'
import CreateAdult from 'pages/Family/Profiles/FamilyProfile/FamilyMembers/Modals/AuthorizedAdult/CreateAdult'

interface AttendanceHeaderProps {
  params: GetEventAttendeesParams
  setParams: React.Dispatch<React.SetStateAction<GetEventAttendeesParams>>
}

const AttendanceHeader = ({ params, setParams }: AttendanceHeaderProps) => {
  const { id }: { id: string } = useParams()
  const {
    attendanceModalOpen,
    setAttendanceModalOpen,
    checkInOutModalOpen,
    setCheckInOutModalOpen,
    checkInModalOpen,
    checkOutModalOpen,
    dropInModalOpen,
    addDropInAttendeeModalOpen,
    dropOutModalOpen,
    accountNotFoundModalOpen,
    accountFoundModalOpen,
    detailedDropOutModalOpen,
    detailedDropInModalOpen,
    approveCheckInModal,
    rollCallModalOpen,
    addNewMemberModalOpen,
    familyId,
    exportTicketModalOpen,
    setExportTicketModalOpen,
    importRegistrationsModalOpen,
    setImportRegistrationsModalOpen,
  } = eventAttendanceStore(store => ({
    attendanceModalOpen: store.attendanceModalOpen,
    setAttendanceModalOpen: store.setAttendanceModalOpen,
    checkInOutModalOpen: store.checkInOutModalOpen,
    setCheckInOutModalOpen: store.setCheckInOutModalOpen,
    checkInModalOpen: store.checkInModalOpen,
    setCheckInModalOpen: store.setCheckInModalOpen,
    checkOutModalOpen: store.checkOutModalOpen,
    dropInModalOpen: store.dropInModalOpen,
    addDropInAttendeeModalOpen: store.addDropInAttendeeModalOpen,
    dropOutModalOpen: store.dropOutModalOpen,
    accountNotFoundModalOpen: store.accountNotFoundModalOpen,
    accountFoundModalOpen: store.accountFoundModalOpen,
    detailedDropOutModalOpen: store.detailedDropOutModalOpen,
    detailedDropInModalOpen: store.detailedDropInModalOpen,
    approveCheckInModal: store.approveCheckInModal,
    rollCallModalOpen: store.rollCallModalOpen,
    addNewMemberModalOpen: store.addNewMemberModalOpen,
    familyId: store.familyId,
    exportTicketModalOpen: store.exportTicketModalOpen,
    setExportTicketModalOpen: store.setExportTicketModalOpen,
    importRegistrationsModalOpen: store.importRegistrationsModalOpen,
    setImportRegistrationsModalOpen: store.setImportRegistrationsModalOpen,
  }))

  const { adultCreationModal } = adultStore(store => ({
    adultCreationModal: store.adultCreationModal,
  }))
  const { kidModalVisible } = kidStore(store => ({
    kidModalVisible: store.kidCreationModal,
  }))

  const handleOpenModal = () => {
    setAttendanceModalOpen(true)
  }

  const {
    data: eventExportData,
    isLoading: isEvnetExportLoading,
    isFetching: isEventExportFetching,
    refetch: refetchEventExport,
    isError: isEventExportError,
  } = useGetEventAttendeesExport(id)

  const {
    data: exportTicketsData,
    isFetching: isTicketsFetching,
    refetch: refetchExportTickets,
  } = useGetExportEventTickets(id)

  const moreOptions = [
    {
      key: '1',
      label: (
        <>
          {isEventExportFetching || isEvnetExportLoading ? (
            <div className="flex justify-center">
              <ButtonSpinner size={18} color="#2ba7ca" />
            </div>
          ) : isEventExportError ? (
            <span>Something went wrong</span>
          ) : (
            <div
              className="flex align-center gap-10px"
              onClick={handleOpenModal}
            >
              <Download size={16} color="#000" /> Export Attendees
            </div>
          )}
        </>
      ),
    },
    {
      key: '2',
      label: (
        <div
          className="flex align-center gap-10px"
          onClick={() => {
            refetchExportTickets()
            setExportTicketModalOpen(true)
          }}
        >
          {isTicketsFetching ? (
            <ButtonSpinner size={16} color="#2ba7ca" />
          ) : (
            <Download size={16} color="#000" />
          )}
          Export Ticket IDs & Days
        </div>
      ),
    },
    {
      key: '3',
      label: (
        <div
          className="flex align-center gap-10px"
          onClick={() => setImportRegistrationsModalOpen(true)}
        >
          <Download size={16} color="#000" style={{ transform: 'rotate(180deg)' }} />
          Import Registrations
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="px-40px py-20px">
        <div className="flex justify-end gap-20px">
          <button className="simple-btn">Contact parents</button>
          <button
            className="simple-btn"
            onClick={() => setCheckInOutModalOpen(true)}
          >
            Check-in/out
          </button>
          <Dropdown menu={{ items: moreOptions }} trigger={['click']}>
            <button
              className="button btn-borderless-gray"
              onClick={() => {
                refetchEventExport()
              }}
            >
              More <CaretDown size={22} color="#000" />
            </button>
          </Dropdown>
          <button className="button btn-primary">New Registration</button>
        </div>

        <div className="pt-30px filters-header">
          <div className="flex gap-8px align-center">
            <div className="flex justify-start align-center gap-8px">
              <button className="button-otuliner-bordered">
                <ListBullets size={20} color="#000" />
              </button>
              <button className="button-otuliner-bordered">
                <MagnifyingGlass size={20} color="#000" />
              </button>
            </div>
            <div className="filter-by">
              <h4 className="text-14 w-500">Filter by:</h4>
              <button className="button-otuliner-bordered">
                Ticket <CaretDown size={20} color="#000" />
              </button>
              <button className="button-otuliner-bordered">
                Attendee age <CaretDown size={20} color="#000" />
              </button>
              <button className="button-otuliner-bordered">
                Gender <CaretDown size={20} color="#000" />
              </button>
              <button className="button-otuliner-bordered">
                School <CaretDown size={20} color="#000" />
              </button>
              <button className="button-otuliner-bordered">
                Grade <CaretDown size={20} color="#000" />
              </button>
              <button className="button-otuliner-bordered">
                Registered date <CaretDown size={20} color="#000" />
              </button>
            </div>
          </div>

          <div className="flex justify-start align-center gap-8px">
            <h4 className="text-14 w-500">Sort by:</h4>
            <button className="button-otuliner-bordered">
              <SortAscending /> First name <CaretDown size={20} color="#000" />
            </button>
          </div>
        </div>
      </div>

      {checkInOutModalOpen && <CheckInOutModal />}
      {attendanceModalOpen && <ExportAttendeesModal data={eventExportData} />}
      {checkInModalOpen && <CheckInModal />}
      {checkOutModalOpen && <CheckOutModal />}
      {dropInModalOpen && <DropInModal />}
      {addDropInAttendeeModalOpen && <AddDropIn />}
      {dropOutModalOpen && <DropOutModal />}
      {accountNotFoundModalOpen && <FamilyNotFound />}
      {accountFoundModalOpen && <FamilyFound />}
      {detailedDropOutModalOpen && <DetailedDropout />}
      {detailedDropInModalOpen && <DropInDetailModal />}
      {approveCheckInModal && <ApproveCheckInModal />}
      {rollCallModalOpen && <RollCallModal />}
      {addNewMemberModalOpen && <AddMember />}
      {kidModalVisible && <KidCreation familyId={familyId} />}
      {adultCreationModal && <CreateAdult familyId={familyId} />}
      {exportTicketModalOpen && (
        <ExportTicketReferenceModal
          data={exportTicketsData}
          isLoading={isTicketsFetching}
        />
      )}
      {importRegistrationsModalOpen && <ImportRegistrationsModal />}
    </>
  )
}

export default AttendanceHeader
